// src/pages/Dashboard.js
import React, { useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Box,
  Tooltip,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { collection, collectionGroup, getDocs } from "firebase/firestore";
import { firestore } from "../firebase";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useQuery } from "@tanstack/react-query";

// Reusable component for a dashboard field with tooltip.
const DashboardField = ({ label, value, tooltipText }) => (
  <Card>
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">{label}</Typography>
        <Tooltip title={tooltipText} arrow>
          <InfoIcon color="action" />
        </Tooltip>
      </Box>
      <Typography variant="h4">{value}</Typography>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  // Timespan selection state
  const [timeSpan, setTimeSpan] = useState("thisMonth");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [customApplied, setCustomApplied] = useState(false);

  // Helper: convert Firestore value to Date.
  const getDateFromValue = (value) => {
    if (!value) return null;
    if (typeof value.toDate === "function") {
      return value.toDate();
    }
    return new Date(value);
  };

  // Compute filter range based on selected timespan.
  const getFilterRange = () => {
    const now = new Date();
    let start, end;
    if (timeSpan === "thisYear") {
      start = new Date(now.getFullYear(), 0, 1);
      end = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
    } else if (timeSpan === "thisMonth") {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    } else if (timeSpan === "thisWeek") {
      const dayOfWeek = now.getDay();
      const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      start = new Date(now);
      start.setDate(now.getDate() + diffToMonday);
      start.setHours(0, 0, 0, 0);
      end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
    } else if (timeSpan === "custom" && customStart && customEnd) {
      start = new Date(customStart);
      start.setHours(0, 0, 0, 0);
      end = new Date(customEnd);
      end.setHours(23, 59, 59, 999);
    } else {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    }
    return { start, end };
  };

  // Fetch metrics data from Firestore.
  const fetchMetrics = async () => {
    const { start: filterStart, end: filterEnd } = getFilterRange();

    // Count accumulators
    let vehiclesCount = 0;
    let bookingsCount = 0;
    let onlineBookingsCount = 0;
    let manualBookingsCount = 0;
    let onlineTotalBookingLength = 0;
    let onlineTotalDailyRate = 0;
    let onlineTotalBookingPrice = 0;
    let onlineTotalRentalBaseCosts = 0;
    let onlineTotalExtras = 0;
    let onlineTotalExtraHoursCost = 0;
    let onlineTotalInsurance = 0;
    let onlineTotalWithInsurance = 0;
    let onlineTotalUsedInsurance = 0;
    let onlineCancelledCount = 0;
    let onlineRefundedCount = 0;
    let totalBookingRefundedAmount = 0;
    let totalInsuranceRefundedAmount = 0;
    let totalOnlineCancellationFees = 0;

    const incomeByDate = {};

    // Newsletter subscribers
    const newsletterSnapshot = await getDocs(
      collection(firestore, "newsletter")
    );
    const newsletterCount = newsletterSnapshot.size;

    // Vehicles count
    const vehiclesSnapshot = await getDocs(
      collectionGroup(firestore, "vehicles")
    );
    vehiclesCount = vehiclesSnapshot.size;

    // Bookings
    const bookingsSnapshot = await getDocs(
      collectionGroup(firestore, "bookings")
    );
    bookingsSnapshot.forEach((doc) => {
      const data = doc.data();
      const method = data.source?.bookingMethod?.toLowerCase();
      const status = data.status;

      const startDate = getDateFromValue(data.startDate);
      const endDate = getDateFromValue(data.endDate);
      if (
        !startDate ||
        !endDate ||
        startDate < filterStart ||
        startDate > filterEnd
      )
        return;

      bookingsCount++;

      const rentalDays = (endDate - startDate) / (1000 * 60 * 60 * 24);
      const rate = data.bill?.dailyRate || 0;
      const extras = data.bill?.extrasCost || 0;
      const insurance = data.bill?.insuranceCost || 0;
      const extraHoursCost = data.bill?.extraHoursCost || 0;
      const rentalBaseCost = data.bill?.baseCost || 0;
      const bookingRevenue = data.bill?.totalCost || 0;

      if (method === "online") {
        onlineBookingsCount++;
        onlineTotalBookingLength += rentalDays;
        onlineTotalDailyRate += rate;

        onlineTotalInsurance += insurance;
        onlineTotalBookingPrice += bookingRevenue;

        if (insurance > 0) {
          onlineTotalWithInsurance++;
        }

        // Add to income by day
        const dateStr = startDate.toISOString().slice(0, 10);
        incomeByDate[dateStr] = (incomeByDate[dateStr] || 0) + bookingRevenue;

        if (status === "cancelled") {
          onlineCancelledCount++;
          if (data.cancellationRefunded) {
            if (insurance > 0) {
              onlineTotalUsedInsurance++;
            }
            onlineRefundedCount++;
            const refundSum = (data.refunds || []).reduce(
              (acc, r) => acc + (r.amount || 0),
              0
            );
            if (data.cancelledBy == "shop") {
              totalInsuranceRefundedAmount += insurance;
            } else {
              const bookingRefund = refundSum - insurance;
              totalBookingRefundedAmount += bookingRefund;
            }
          } else {
            totalOnlineCancellationFees += bookingRevenue - insurance;
          }
        } else {
          onlineTotalExtras += extras;
          onlineTotalExtraHoursCost += extraHoursCost;
          onlineTotalRentalBaseCosts += rentalBaseCost;
        }
      } else if (method === "manual") {
        manualBookingsCount++;
      }
    });

    // Shops and affiliates
    const shopsSnapshot = await getDocs(collection(firestore, "rentalShops"));
    const shopsCount = shopsSnapshot.size;

    const affiliatesSnapshot = await getDocs(
      collection(firestore, "affiliates")
    );
    const affiliatesCount = affiliatesSnapshot.size;

    let affiliateRevenue = 0;
    for (const shopDoc of shopsSnapshot.docs) {
      const shop = shopDoc.data();
      if (shop.meta?.affiliateId) {
        const bookings = await getDocs(collection(shopDoc.ref, "bookings"));
        bookings.forEach((doc) => {
          const b = doc.data();
          const method = b.source?.bookingMethod?.toLowerCase();
          const start = getDateFromValue(b.startDate);
          const end = getDateFromValue(b.endDate);
          if (!start || !end || method !== "online") return;
          if (start >= filterStart && start <= filterEnd) {
            if (b.status != "cancelled") {
              affiliateRevenue +=
                (b.bill?.totalCost - b.bill?.insuranceCost) * 0.02;
            }
          }
        });
      }
    }

    // Platform financials
    const totalAmount = onlineTotalBookingPrice;
    const totalInsuranceAmount = onlineTotalInsurance;
    const totalBookingRefundAmount = totalBookingRefundedAmount;
    const totalInsuranceRefundAmount = totalInsuranceRefundedAmount;
    const totalRefundAmount =
      totalBookingRefundAmount + totalInsuranceRefundAmount;

    const totalExtrasCost = onlineTotalExtras;
    const totalExtraHoursCost = onlineTotalExtraHoursCost;
    const totalBookingBaseCost = onlineTotalRentalBaseCosts;

    const paymentFees = totalAmount * 0.017 + onlineBookingsCount * 12.5;

    const totalSplittableIncome =
      totalAmount - totalRefundAmount - totalInsuranceAmount;

    const shopsDueBookins = totalSplittableIncome * 0.85;
    const shopsDueInsurance = totalInsuranceAmount * 0.5;
    const shopsDue = shopsDueBookins + shopsDueInsurance;

    const elyCommission = totalSplittableIncome * 0.03;
    const yannisSalary = totalSplittableIncome * 0.03;
    const platformProfits =
      totalSplittableIncome -
      shopsDueBookins -
      paymentFees -
      yannisSalary -
      elyCommission -
      affiliateRevenue +
      totalInsuranceAmount * 0.5;
    const netAfterRefunds = totalAmount - totalRefundAmount;
    const netFinalIncome = netAfterRefunds - paymentFees;

    const totalBookingWithInsuranceText = `${onlineTotalWithInsurance} (${(
      (onlineTotalWithInsurance / onlineBookingsCount || 0) * 100
    ).toFixed(0)}%)`;

    console.log("totalInsuranceAmount: ", totalInsuranceAmount);

    // Averages
    const avgBookingLength =
      onlineBookingsCount > 0
        ? (onlineTotalBookingLength / onlineBookingsCount).toFixed(2)
        : "0";
    const avgDailyRate =
      onlineBookingsCount > 0
        ? (onlineTotalDailyRate / onlineBookingsCount).toFixed(2)
        : "0";
    const avgBookingPrice =
      onlineBookingsCount > 0
        ? (onlineTotalBookingPrice / onlineBookingsCount).toFixed(2)
        : "0";

    const graphData = Object.entries(incomeByDate)
      .map(([date, income]) => ({ date, income }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    return {
      metrics: {
        vehiclesCount,
        bookingsCount,
        shopsCount,
        onlineBookingsCount,
        manualBookingsCount,
        affiliatesCount,
        newsletterCount,
        onlineCancelledCount,
        onlineCancellationFees: totalOnlineCancellationFees.toFixed(2),
        onlineRefundedCount,
        totalRefundedAmount: totalRefundAmount.toFixed(2),
        avgBookingLength,
        avgDailyRate,
        avgBookingPrice,
        extrasIncome: totalExtrasCost.toFixed(2),
        totalUsedInsuranceCount: onlineTotalUsedInsurance,
        totalBookingWithInsurance: onlineTotalWithInsurance,
        totalBookingWithInsuranceText,
        insuranceIncome: totalInsuranceAmount.toFixed(2),
        totalAmount: totalAmount.toFixed(2),
        totalBookingBaseCost: totalBookingBaseCost.toFixed(2),
        totalExtrasCost: totalExtrasCost.toFixed(2),
        totalExtraHoursCost: totalExtraHoursCost.toFixed(2),
        netAfterRefunds: netAfterRefunds.toFixed(2),
        netFinalIncome: netFinalIncome.toFixed(2),
        shopsDue: shopsDue.toFixed(2),
        shopsDueBookins,
        shopsDueInsurance,
        paymentFees: paymentFees.toFixed(2),
        elyCommission: elyCommission.toFixed(2),
        yannisSalary: yannisSalary.toFixed(2),
        affiliateRevenue: affiliateRevenue.toFixed(2),
        platformProfits: platformProfits.toFixed(2),
      },
      graphData,
    };
  };

  // Use React Query's useQuery (object syntax)
  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      "dashboardMetrics",
      timeSpan,
      customApplied,
      customStart,
      customEnd,
    ],
    queryFn: fetchMetrics,
    keepPreviousData: true,
  });

  // Handler for custom range "Show" button.
  const handleCustomShow = () => {
    if (customStart && customEnd) {
      setCustomApplied(true);
      refetch();
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
        }}
      >
        <Header />
        <Container style={{ marginTop: "20px", flexGrow: 1 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <FormControl variant="outlined" style={{ minWidth: 200 }}>
                <InputLabel id="time-span-label">Time Span</InputLabel>
                <Select
                  labelId="time-span-label"
                  value={timeSpan}
                  onChange={(e) => {
                    setTimeSpan(e.target.value);
                    if (e.target.value !== "custom") {
                      setCustomStart("");
                      setCustomEnd("");
                      setCustomApplied(false);
                    }
                  }}
                  label="Time Span"
                >
                  <MenuItem value="thisYear">This Year</MenuItem>
                  <MenuItem value="thisMonth">This Month</MenuItem>
                  <MenuItem value="thisWeek">This Week</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {timeSpan === "custom" && (
              <>
                <Grid item>
                  <TextField
                    label="Start Date"
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    label="End Date"
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item>
                  <Button variant="contained" onClick={handleCustomShow}>
                    Show
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
          {isLoading ? (
            <CircularProgress style={{ marginTop: "20px" }} />
          ) : data ? (
            <>
              <Grid container spacing={3} style={{ marginTop: "20px" }}>
                {/* Basic counts */}
                <Grid item xs={12} sm={6} md={4}>
                  <DashboardField
                    label="Rental Shops Count"
                    value={data.metrics.shopsCount}
                    tooltipText="Total number of rental shops registered."
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <DashboardField
                    label="Vehicles Count"
                    value={data.metrics.vehiclesCount}
                    tooltipText="Total number of vehicles across all rental shops."
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <DashboardField
                    label="Bookings Count"
                    value={data.metrics.bookingsCount}
                    tooltipText="Total number of bookings made."
                  />
                </Grid>
                {/* Booking method counts */}
                <Grid item xs={12} sm={6} md={4}>
                  <DashboardField
                    label="Online Bookings"
                    value={data.metrics.onlineBookingsCount}
                    tooltipText="Number of bookings made online."
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <DashboardField
                    label="Manual Bookings"
                    value={data.metrics.manualBookingsCount}
                    tooltipText="Number of bookings made manually."
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <DashboardField
                    label="Affiliates Count"
                    value={data.metrics.affiliatesCount}
                    tooltipText="Total number of affiliates."
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <DashboardField
                    label="Newsletter Subscribers"
                    value={data.metrics.newsletterCount}
                    tooltipText="Total users subscribed to the newsletter."
                  />
                </Grid>
              </Grid>
              <br />
              <br />
              <Typography variant="h6" mt={4} mb={2}>
                ({data.metrics.onlineBookingsCount}) Online Bookings Data:
              </Typography>
              {/* Online Booking Averages & Platform Data */}
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <DashboardField
                    label="Avg Booking Length"
                    value={data.metrics.avgBookingLength}
                    tooltipText="Average length of online bookings in days."
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <DashboardField
                    label="Avg Daily Rate"
                    value={`${data.metrics.avgDailyRate}₱`}
                    tooltipText="Average daily rental rate for online bookings."
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <DashboardField
                    label="Avg Booking Price"
                    value={`${data.metrics.avgBookingPrice}₱`}
                    tooltipText="Average total price for online bookings."
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <DashboardField
                    label="Online Bookings Count"
                    value={data.metrics.onlineBookingsCount}
                    tooltipText="Total number of online bookings."
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <DashboardField
                    label="Online Bookings Cancelled"
                    value={data.metrics.onlineCancelledCount}
                    tooltipText="Number of online bookings that were cancelled."
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <DashboardField
                    label="Booking with insurance"
                    value={data.metrics.totalBookingWithInsuranceText}
                    tooltipText="Bookings that purchased cancellation insurance."
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <DashboardField
                    label="Booking with used insurance"
                    value={data.metrics.totalUsedInsuranceCount}
                    tooltipText="Bookings that used cancellation insurance."
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <DashboardField
                    label="Refunded Bookings"
                    value={data.metrics.onlineRefundedCount}
                    tooltipText="Online bookings with refund after cancellation."
                  />
                </Grid>
              </Grid>
              <Typography variant="h6" mt={4} mb={2}>
                Income:
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <DashboardField
                    label="Total Amount Made"
                    value={`${data.metrics.totalAmount}₱`}
                    tooltipText="Total revenue generated from online bookings."
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <DashboardField
                    label="Bookings Base Income"
                    value={`${data.metrics.totalBookingBaseCost}₱`}
                    tooltipText="Total made from bookings base Income (daily rate * booked days)."
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <DashboardField
                    label="Bookings Extra Hours Income"
                    value={`${data.metrics.totalExtraHoursCost}₱`}
                    tooltipText="Total made from booking extra hours costs."
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <DashboardField
                    label="Bookings Extras Income"
                    value={`${data.metrics.totalExtrasCost}₱`}
                    tooltipText="Total made from booking extras."
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <DashboardField
                    label="Bookings Insurance Income"
                    value={`${data.metrics.insuranceIncome}₱`}
                    tooltipText="Total made from bookings cancellation insurances."
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <DashboardField
                    label="Cancellation Fees Income"
                    value={data.metrics.onlineCancellationFees}
                    tooltipText="Cancelled online bookings without a refund."
                  />
                </Grid>
              </Grid>
              <Typography variant="h6" mt={4} mb={2}>
                Payouts:
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <DashboardField
                    label="Total Refunded Amount"
                    value={`${data.metrics.totalRefundedAmount}₱`}
                    tooltipText="Total amount refunded for cancelled online bookings."
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <DashboardField
                    label="Payment Fees"
                    value={`${data.metrics.paymentFees}₱`}
                    tooltipText="Payment fees calculated as 1.7% plus a fixed fee per transaction (converted to pesos)."
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <DashboardField
                    label="Amount Made by Affiliates"
                    value={`${data.metrics.affiliateRevenue}₱`}
                    tooltipText="Total revenue for affiliates (2% of online bookings from shops with an affiliate)."
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <DashboardField
                    label="Total Rental Shops payout"
                    value={`${data.metrics.shopsDue}₱`}
                    tooltipText="85% of total online booking revenue and 50% of insurance income, to be paid out to rental shops."
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <DashboardField
                    label="Shops Booking income"
                    value={`${data.metrics.shopsDueBookins}₱`}
                    tooltipText="85% of total online booking revenue, to be paid out to rental shops."
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <DashboardField
                    label="Shops Insurance income"
                    value={`${data.metrics.shopsDueInsurance}₱`}
                    tooltipText="50% of total online revenue, to be paid out to rental shops."
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <DashboardField
                    label="Ely Commission"
                    value={`${data.metrics.elyCommission}₱`}
                    tooltipText="Commission for Ely at 3% of online revenue."
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <DashboardField
                    label="Yannis Salary"
                    value={`${data.metrics.yannisSalary}₱`}
                    tooltipText="Salary for Yannis at 3% of online revenue."
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <DashboardField
                    label="Platform Profits"
                    value={`${data.metrics.platformProfits}₱`}
                    tooltipText="Total online revenue minus the payouts (around 5% of total)."
                  />
                </Grid>
              </Grid>
              <Typography
                variant="h5"
                align="center"
                gutterBottom
                style={{ marginTop: "40px" }}
              >
                Generated Income by Day
              </Typography>
              {data.graphData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={data.graphData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="income"
                      stroke="#8884d8"
                      activeDot={{ r: 8 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Typography align="center">No income data available</Typography>
              )}
            </>
          ) : null}
        </Container>
        <Footer />
      </Box>
    </>
  );
};

export default Dashboard;
